/**
 * 工单详情 —— 核心工作页，依据 design/02-case-detail-tablet.png。
 *
 * 页面唯一的大号青色按钮是「完成这一步 ✓」，这是本页 90% 的使用目的。
 * 余额是第二醒目的元素。
 * 完成一步后立刻弹出「约下次」—— 预约永远不会漏排的关键。
 */

import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Ban,
  Camera,
  Check,
  Copy,
  Link2,
  MessageCircle,
  Phone,
  Plus,
  Receipt,
  Info,
  AlertTriangle,
  Trash2,
} from 'lucide-react'
import { api, type CaseDetail, type Me } from '../lib/api'
import {
  ARCH_ZH,
  CASE_STATUS_ZH,
  CASE_TYPE_ZH,
  PAYMENT_KIND_ZH,
  PAYMENT_METHOD_ZH,
  date as fmtDate,
  dateShort,
  rm,
} from '../lib/format'
import {
  Badge,
  Button,
  Card,
  CheckMark,
  Modal,
  Skeleton,
  caseStatusTone,
  cx,
  useToast,
} from '../components/ui'
import { ToothChart } from '../components/ToothChart'
import { AnimatedAmount } from '../components/AnimatedAmount'
import { feedback } from '../lib/feedback'
import { PaymentDrawer } from '../features/PaymentDrawer'
import { AppointmentDrawer } from '../features/AppointmentDrawer'

export function CaseDetailPage({ me }: { me: Me }) {
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()

  const [payOpen, setPayOpen] = useState(false)
  const [apptOpen, setApptOpen] = useState(false)
  const [nextStepHint, setNextStepHint] = useState<{ title: string; date: string; stepId: string } | null>(
    null,
  )
  const [voidTarget, setVoidTarget] = useState<string | null>(null)
  const [voidReason, setVoidReason] = useState('')
  const [delOpen, setDelOpen] = useState(false)
  const [delReason, setDelReason] = useState('')
  const [delForce, setDelForce] = useState(false)
  /** 刚完成的那一步，用来闪一下绿确认「动的是这行」 */
  const [justDone, setJustDone] = useState<string | null>(null)

  const q = useQuery({
    queryKey: ['case', id],
    queryFn: () => api.get<{ case: CaseDetail }>(`/api/cases/${id}`),
  })

  const completeStep = useMutation({
    mutationFn: (stepId: string) =>
      api.post<{
        nextStep: {
          id: string
          title: string
          suggestedDate: string
          needsAppointment: boolean
        } | null
      }>(`/api/cases/${id}/steps/${stepId}/complete`),
    onSuccess: (r, stepId) => {
      feedback('done')
      setJustDone(stepId)
      setTimeout(() => setJustDone(null), 1000)
      void qc.invalidateQueries({ queryKey: ['case', id] })
      void qc.invalidateQueries({ queryKey: ['today'] })
      // 只有下一步真的需要顾客到店才弹预约框。
      // 「生产中」是店里自己的活，弹出来纯属打扰。
      if (r.nextStep?.needsAppointment) {
        setNextStepHint({
          title: r.nextStep.title,
          date: r.nextStep.suggestedDate,
          stepId: r.nextStep.id,
        })
      } else {
        toast.show(r.nextStep ? `已完成 · 下一步：${r.nextStep.title}` : '已完成')
      }
    },
    onError: (e) => {
      feedback('error')
      toast.show((e as Error).message, { tone: 'bad' })
    },
  })

  const setStatus = useMutation({
    mutationFn: (status: string) => api.post(`/api/cases/${id}/status`, { status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['case', id] })
      void qc.invalidateQueries({ queryKey: ['today'] })
      toast.show('已更新')
    },
    onError: (e) => toast.show((e as Error).message, { tone: 'bad' }),
  })

  const makeLink = useMutation({
    mutationFn: (_mode: 'progress' | 'receipt' = 'progress') =>
      api.post<{
        url: string
        messageText: string
        whatsappUrl: string
        receiptUrl: string
        receiptText: string
        receiptWhatsappUrl: string
      }>(`/api/cases/${id}/portal-link`),
    onSuccess: async (r, mode) => {
      const receipt = mode === 'receipt'
      void qc.invalidateQueries({ queryKey: ['case', id] })
      void navigator.clipboard.writeText(receipt ? r.receiptText : r.messageText)
      window.open(receipt ? r.receiptWhatsappUrl : r.whatsappUrl, '_blank', 'noopener')

      // 刚把消息发给顾客了 —— 这张工单上待通知的事一并归零，
      // 否则提醒中心还会催同一件事，清单很快就没人信了。
      const { cleared } = await api.post<{ cleared: number }>(`/api/cases/${id}/notified`)
      void qc.invalidateQueries({ queryKey: ['reminder-counts'] })
      void qc.invalidateQueries({ queryKey: ['reminders'] })
      void qc.invalidateQueries({ queryKey: ['today'] })
      toast.show(
        cleared > 0
          ? `文案已复制 · 顺便把 ${cleared} 条待通知也划掉了`
          : receipt
            ? '收据已生成，文案已复制'
            : '链接已生成，文案已复制',
      )
    },
    onError: (e) => toast.show((e as Error).message, { tone: 'bad' }),
  })

  const deleteCase = useMutation({
    mutationFn: () =>
      api.del<{ caseNo: string }>(`/api/cases/${id}`, {
        reason: delReason.trim(),
        force: delForce,
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['today'] })
      void qc.invalidateQueries({ queryKey: ['cases'] })
      void qc.invalidateQueries({ queryKey: ['customer'] })
      toast.show(`工单 ${r.caseNo} 已删除`)
      navigate('/cases', { replace: true })
    },
    onError: (e) => {
      // 有收款时后端会拒一次，勾选确认后才放行
      if ((e as { code?: string }).code === 'CASE_HAS_PAYMENTS') setDelForce(true)
      toast.show((e as Error).message, { tone: 'bad' })
    },
  })

  const voidPayment = useMutation({
    mutationFn: (paymentId: string) =>
      api.post(`/api/payments/${paymentId}/void`, { reason: voidReason.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['case', id] })
      setVoidTarget(null)
      setVoidReason('')
      toast.show('已作废')
    },
    onError: (e) => toast.show((e as Error).message, { tone: 'bad' }),
  })

  if (q.isLoading) return <CaseSkeleton />
  if (q.isError)
    return (
      <p className="py-12 text-center text-[17px] text-ink-2">
        {(q.error as Error).message}
      </p>
    )
  if (!q.data) return null

  const c = q.data.case
  const isOwner = me.user.role === 'OWNER'
  const current = c.steps.find((s) => s.status === 'PENDING')
  const activePayments = c.payments.filter((p) => !p.voidedAt).length
  const overdue =
    c.daysUntilPromised !== null && c.daysUntilPromised < 0 && c.status !== 'DELIVERED' && c.status !== 'CLOSED'

  return (
    <div className="mx-auto max-w-[1120px] pb-24 lg:pb-0">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="flex h-11 items-center gap-1.5 text-[17px] text-ink-2 hover:text-ink"
        >
          <ArrowLeft size={20} /> 工单
        </button>
        <h1 className="tnum text-[28px] font-bold">{c.caseNo}</h1>
        <Badge tone={caseStatusTone(c.status)}>{CASE_STATUS_ZH[c.status]}</Badge>
        {overdue && (
          <Badge tone="bad" icon={<AlertTriangle size={14} />}>
            逾期 {-c.daysUntilPromised!} 天
          </Badge>
        )}
        {isOwner && (
          <Button
            size="sm"
            variant="ghost"
            icon={<Trash2 size={18} />}
            aria-label="删除这张工单"
            className="ml-auto text-ink-3 hover:text-bad"
            onClick={() => {
              setDelReason('')
              setDelForce(false)
              setDelOpen(true)
            }}
          >
            删除
          </Button>
        )}
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,42fr)_minmax(0,58fr)]">
        {/* ── 左列 ── */}
        <div className="flex flex-col gap-4">
          <Card>
            <button
              onClick={() => navigate(`/customers/${c.customer.id}`)}
              className="text-left text-[19px] font-semibold hover:text-brand-700"
            >
              {c.customer.fullName} →
            </button>
            <div className="mt-1 flex items-center gap-3">
              <a
                href={`tel:${c.customer.phoneE164}`}
                className="tnum text-[17px] font-medium text-brand-700 hover:underline"
              >
                {c.customer.phoneE164}
              </a>
              <Button size="sm" icon={<Phone size={16} />} onClick={() => window.open(`tel:${c.customer.phoneE164}`)}>
                拨打
              </Button>
            </div>

            {c.customer.healthNotes && (
              <p className="mt-3 flex gap-2 rounded-md border-l-[3px] border-info bg-info-bg px-3 py-2.5 text-[16px] text-info">
                <Info size={18} className="mt-0.5 shrink-0" />
                {c.customer.healthNotes}
              </p>
            )}

            <div className="my-4 border-t border-line" />

            <p className="text-[17px]">
              {c.typeLabel}
              {c.arch ? ` · ${ARCH_ZH[c.arch]}` : ''}
            </p>
            <p className="tnum mt-1 text-[17px]">
              <span className="text-ink-2">交期 </span>
              {fmtDate(c.promisedDate)}
              {c.daysUntilPromised !== null && (
                <span className={cx('ml-2', overdue ? 'font-semibold text-bad' : 'text-ink-3')}>
                  {c.daysUntilPromised >= 0 ? `（还有 ${c.daysUntilPromised} 天）` : `（逾期 ${-c.daysUntilPromised} 天）`}
                </span>
              )}
            </p>

            <div className="my-4 border-t border-line" />

            <dl className="tnum text-[17px]">
              <Money label="成交价" value={rm(c.finalAmount)} />
              <Money label="已收" value={rm(c.paidAmount)} />
              <div className="my-2 border-t-2 border-line-strong" />
              <div className="flex items-center justify-between py-1">
                <dt className="text-[21px] font-bold">余额</dt>
                <dd className="flex items-center gap-2 text-[21px] font-bold">
                  <AnimatedAmount value={c.balanceAmount} />
                  {c.balanceAmount > 0 && <AlertTriangle size={20} className="text-warn" />}
                </dd>
              </div>
            </dl>

            <Button block size="lg" className="mt-4" onClick={() => setPayOpen(true)}>
              记一笔收款
            </Button>
          </Card>

          <Card title="牙位图">
            <ToothChart value={c.toothMap} readOnly />
          </Card>

          {c.notes && (
            <Card title="备注">
              <p className="text-[17px] whitespace-pre-wrap">{c.notes}</p>
            </Card>
          )}
        </div>

        {/* ── 右列 ── */}
        <div className="flex flex-col gap-4">
          <Card title="流程步骤">
            <ol className="flex flex-col">
              {c.steps.map((s, i) => {
                const isCurrent = current?.id === s.id
                return (
                  <li
                    key={s.id}
                    className={cx(
                      'relative flex gap-3 rounded-md pb-4 last:pb-0',
                      justDone === s.id && 'gb-flash-ok',
                    )}
                  >
                    {i < c.steps.length - 1 && (
                      <span className="absolute left-[13px] top-7 h-[calc(100%-1.75rem)] w-0.5 bg-line" />
                    )}
                    <span
                      className={cx(
                        'relative z-1 flex size-7 shrink-0 items-center justify-center rounded-full',
                        s.status === 'DONE'
                          ? 'bg-ok text-white'
                          : isCurrent
                            ? 'bg-brand-600 text-white'
                            : 'border-2 border-line-strong bg-white',
                      )}
                    >
                      {s.status === 'DONE' ? (
                        <CheckMark size={16} />
                      ) : isCurrent ? (
                        <span className="size-2 rounded-full bg-white" />
                      ) : null}
                    </span>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className={cx('text-[17px]', isCurrent && 'font-semibold')}>{s.title}</span>
                        {/*
                          这里原本显示完成日期 / 计划日期，已经去掉。
                          店里是【月尾一次性补录整月的单】，点「完成」的那一刻
                          是录入时间，不是活干完的时间 —— 把它显示出来，
                          等于在工单上写一个假日期。
                          时间仍然记在数据库里（doneAt / 审计），只是不再当作
                          「这步什么时候做的」摆给人看。
                        */}
                        {isCurrent && (
                          <span className="shrink-0 text-[15px] text-ink-3">现在</span>
                        )}
                      </div>

                      {isCurrent && (
                        <Button
                          block
                          size="lg"
                          variant="primary"
                          // 手机上底部有固定操作条，这里再放一个就是重复
                          className="mt-2.5 hidden lg:flex"
                          loading={completeStep.isPending}
                          onClick={() => completeStep.mutate(s.id)}
                        >
                          完成这一步 ✓
                        </Button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ol>

            {c.status !== 'CLOSED' && c.status !== 'CANCELLED' && (
              <div className="mt-3 flex flex-wrap gap-2 border-t border-line pt-3">
                {c.status === 'IN_PROGRESS' && (
                  <Button size="sm" onClick={() => setStatus.mutate('READY')}>
                    标记做好了
                  </Button>
                )}
                {(c.status === 'READY' || c.status === 'IN_PROGRESS') && (
                  <Button size="sm" onClick={() => setStatus.mutate('DELIVERED')}>
                    标记已交付
                  </Button>
                )}
                <Button size="sm" icon={<Plus size={16} />} onClick={() => setApptOpen(true)}>
                  约下次
                </Button>
              </div>
            )}
          </Card>

          <Card title="收款记录">
            {c.payments.length === 0 ? (
              <p className="py-2 text-[16px] text-ink-3">还没有收款</p>
            ) : (
              <ul className="tnum flex flex-col">
                {c.payments.map((p) => (
                  <li
                    key={p.id}
                    className={cx(
                      'flex items-center gap-3 border-b border-line py-2.5 text-[17px] last:border-0',
                      p.voidedAt && 'text-ink-3 line-through',
                    )}
                  >
                    <span className="w-14 shrink-0 text-ink-2">{dateShort(p.paidAt)}</span>
                    <span className="w-12 shrink-0">{PAYMENT_KIND_ZH[p.kind]}</span>
                    <span className="flex-1 truncate text-ink-2">
                      {PAYMENT_METHOD_ZH[p.method]}
                    </span>
                    <span className="font-semibold">{rm(p.amount)}</span>
                    {isOwner && !p.voidedAt && (
                      <button
                        onClick={() => setVoidTarget(p.id)}
                        aria-label="作废这笔收款"
                        className="flex size-9 shrink-0 items-center justify-center rounded-sm text-ink-3 hover:bg-bad-bg hover:text-bad"
                      >
                        <Ban size={17} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card
            title={`照片 (${c.photos.length})`}
            action={
              <Button size="sm" icon={<Camera size={16} />} disabled>
                拍照
              </Button>
            }
          >
            {c.photos.length === 0 ? (
              <p className="py-2 text-[16px] text-ink-3">还没有照片</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {c.photos.map((p) => (
                  <img
                    key={p.id}
                    src={p.thumbUrl}
                    alt=""
                    className="size-22 rounded-md border border-line object-cover"
                  />
                ))}
              </div>
            )}
          </Card>

          <Card title="顾客链接">
            {c.portalLink ? (
              <>
                <p className="text-[16px] text-ink-2">
                  已生成 · 有效期至 {fmtDate(c.portalLink.expiresAt)}
                  {c.portalLink.lastAccessedAt
                    ? ` · 顾客最后查看 ${fmtDate(c.portalLink.lastAccessedAt)}`
                    : ' · 顾客还没打开过'}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    icon={<MessageCircle size={16} />}
                    loading={makeLink.isPending}
                    onClick={() => makeLink.mutate('progress')}
                  >
                    重新发送
                  </Button>
                  <Button
                    size="sm"
                    icon={<Receipt size={16} />}
                    loading={makeLink.isPending}
                    onClick={() => makeLink.mutate('receipt')}
                  >
                    发收据
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button
                  icon={<Link2 size={18} />}
                  loading={makeLink.isPending}
                  onClick={() => makeLink.mutate('progress')}
                >
                  发进度链接
                </Button>
                {/* 收据和进度页共用同一个 token，顾客手上只有一条链接 */}
                <Button
                  icon={<Receipt size={18} />}
                  loading={makeLink.isPending}
                  onClick={() => makeLink.mutate('receipt')}
                >
                  发收据
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/*
        手机端固定操作条。
        小屏是单列堆叠，「完成这一步」被压在概要与牙位图下面，要滚半屏才够得着 ——
        而那是本页 90% 的使用目的。固定在底部，一进页面就能点。
      */}
      {current && c.status !== 'CLOSED' && c.status !== 'CANCELLED' && (
        <div className="fixed inset-x-0 bottom-15 z-30 border-t border-line bg-white/95 px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+12px)] backdrop-blur lg:hidden">
          <Button
            block
            size="lg"
            variant="primary"
            loading={completeStep.isPending}
            onClick={() => completeStep.mutate(current.id)}
          >
            完成「{current.title}」✓
          </Button>
        </div>
      )}

      <PaymentDrawer
        open={payOpen}
        onClose={() => setPayOpen(false)}
        caseId={c.id}
        balanceAmount={c.balanceAmount}
        onDone={(r) => {
          if (r.suggestion?.code === 'SETTLED_MARK_DELIVERED') {
            setStatus.mutate('DELIVERED')
          }
        }}
      />

      <AppointmentDrawer
        open={apptOpen || nextStepHint !== null}
        onClose={() => {
          setApptOpen(false)
          setNextStepHint(null)
        }}
        customerId={c.customer.id}
        customerName={c.customer.fullName}
        caseId={c.id}
        caseStepId={nextStepHint?.stepId}
        suggestedDate={nextStepHint?.date}
        headline={nextStepHint ? `下一步：${nextStepHint.title}` : undefined}
      />

      <Modal
        open={delOpen}
        onClose={() => setDelOpen(false)}
        title={`删除工单 ${c.caseNo}？`}
        footer={
          <>
            <Button size="lg" onClick={() => setDelOpen(false)}>
              取消
            </Button>
            <Button
              size="lg"
              variant="danger"
              disabled={delReason.trim().length < 2 || (activePayments > 0 && !delForce)}
              loading={deleteCase.isPending}
              onClick={() => deleteCase.mutate()}
            >
              确认删除
            </Button>
          </>
        }
      >
        <p className="mb-3">
          这张单会被<strong>彻底删除</strong>，连同步骤、照片、顾客链接一起消失，无法恢复。
        </p>

        {activePayments > 0 && (
          <label className="mb-3 flex cursor-pointer items-start gap-3 rounded-md border border-bad-line bg-bad-bg px-4 py-3">
            <input
              type="checkbox"
              checked={delForce}
              onChange={(e) => setDelForce(e.target.checked)}
              className="mt-1 size-5.5 shrink-0 accent-bad"
            />
            <span className="text-[16px] text-bad">
              这张单有 {activePayments} 笔收款共 {rm(c.paidAmount)}，删除后账目里也会消失。
              我确认要删。
            </span>
          </label>
        )}

        <input
          autoFocus
          value={delReason}
          onChange={(e) => setDelReason(e.target.value)}
          placeholder="删除原因（例：测试单）"
          className="h-12 w-full rounded-md border border-line-strong px-3.5 text-[17px] focus:border-brand-600 focus:outline-none"
        />
        <p className="mt-2 text-[15px] text-ink-3">删除记录会留在审计日志里。</p>
      </Modal>

      <Modal
        open={voidTarget !== null}
        onClose={() => setVoidTarget(null)}
        title="作废这笔收款？"
        footer={
          <>
            <Button size="lg" onClick={() => setVoidTarget(null)}>
              取消
            </Button>
            <Button
              size="lg"
              variant="danger"
              disabled={voidReason.trim().length < 4}
              loading={voidPayment.isPending}
              onClick={() => voidTarget && voidPayment.mutate(voidTarget)}
            >
              确认作废
            </Button>
          </>
        }
      >
        <p className="mb-3">收款不能修改，只能作废后重录。这条操作会留在审计日志里。</p>
        <input
          autoFocus
          value={voidReason}
          onChange={(e) => setVoidReason(e.target.value)}
          placeholder="作废原因（至少 4 个字）"
          className="h-12 w-full rounded-md border border-line-strong px-3.5 text-[17px] focus:border-brand-600 focus:outline-none"
        />
      </Modal>
    </div>
  )
}

function Money({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-1">
      <dt className="text-ink-2">{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}

function CaseSkeleton() {
  return (
    <div className="mx-auto max-w-[1120px] pb-24 lg:pb-0">
      <Skeleton className="mb-4 h-9 w-64" />
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton className="h-[26rem]" />
        <Skeleton className="h-[26rem]" />
      </div>
    </div>
  )
}
